import {
  isNativeApp,
  requestCoords,
} from "@erinfo/react-utils/src/plugins/native-app"

interface ICoords {
  longitude: number
  latitude: number
}

/**
 * Resolve the current coordinates, or "undefined" when they are unavailable (permission denied,
 * timeout, or an unsupported browser). Callers must handle "undefined" rather than assume a fix.
 *
 * Inside the native app we ask the native side instead of using "navigator.geolocation": the web app
 * is served from a "file://" bundle, so WebKit would show the raw bundle path as the requesting
 * origin instead of the app name.
 */
export const getCoords = async (): Promise<
  { long: number; lat: number } | undefined
> => {
  if (isNativeApp()) {
    const result = await requestCoords()
    if (result === `denied`) return undefined // respect the denial; retrying here would re-prompt
    if (result) return result
    /**
     * No reply at all: an OTA web bundle can run on an older native binary that has no handler for
     * this command. Fall back to the browser API so location still works on those installs.
     */
  }

  if (!navigator.geolocation) return undefined

  try {
    const pos = await new Promise<{ coords: ICoords }>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject)
    })

    return {
      long: pos.coords.longitude,
      lat: pos.coords.latitude,
    }
  } catch {
    return undefined // ex. the user denied the permission prompt
  }
}
